import { useNavigate } from "react-router-dom";
import { Open } from "./compoment/icon";
import { setPresetStation } from "./redux/slices/stationSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { clearSessionStorage } from "./utils";
import {
  aiSelfieCategoryBackgroundUrl,
  aiSelfieCategoryImageUrl,
  getActiveAiSelfieCategories,
  type AiSelfieCategory,
} from "./services/aiSelfieCategoryService";
import { getActiveAiSelfieArtsByCategory } from "./services/aiSelfieArtService";
import Loader from "./compoment/ui/Loader";
import {
  enrichStationWithThemeBackgrounds,
  isFifaWorldCup2026Station,
  persistSelectedAiSelfieArt,
  persistStationSelection,
  prepareStationFlowLocalStorage,
  preloadFifaWorldCupSelectedArt,
  resetHomeAppState,
} from "./utils/stationFlowStorage";

interface ApiStationItem {
  id: string;
  name: string;
  imgURL: string;
  backgroundImageUrl: string;
  type: string;
  href: string;
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [stations, setStations] = useState<ApiStationItem[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [openingCategory, setOpeningCategory] = useState<ApiStationItem | null>(
    null
  );

  // Full reset on home — clear stationData, selectedStation, selectedAiSelfieArt, and session
  useEffect(() => {
    resetHomeAppState(dispatch);
    clearSessionStorage();
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const res = await getActiveAiSelfieCategories();
      if (cancelled) return;

      if (!res.success || res.data.length === 0) {
        setStations([]);
        setCategoriesError(res.error || "No active categories found");
        setCategoriesLoading(false);
        return;
      }

      const apiStations: ApiStationItem[] = res.data
        .map((cat: AiSelfieCategory) => {
          const slug = (cat.slug || slugify(cat.name || "")).trim();
          if (!slug) return null;
          return {
            id: cat.id,
            name: cat.name,
            imgURL: aiSelfieCategoryImageUrl(cat),
            backgroundImageUrl: aiSelfieCategoryBackgroundUrl(cat),
            type: slug,
            href: "/all",
          };
        })
        .filter((item): item is ApiStationItem => Boolean(item));

      setStations(apiStations);
      setCategoriesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const openCategory = async (item: ApiStationItem) => {
    if (openingCategory) return;

    setOpeningCategory(item);
    clearSessionStorage();
    const storedStation = prepareStationFlowLocalStorage(item);
    dispatch(setPresetStation(storedStation));

    try {
      if (isFifaWorldCup2026Station(storedStation)) {
        await preloadFifaWorldCupSelectedArt(storedStation);
        navigate(`/${item.type}${item.href}`);
        return;
      }

      const artsRes = await getActiveAiSelfieArtsByCategory(String(item.id));
      const arts = artsRes.success ? artsRes.data : [];

      // More than one active art → category picker (/all).
      // Otherwise skip straight to that art's idle screen.
      if (arts.length > 1) {
        navigate(`/${item.type}/all`);
        return;
      }

      const singleArt = arts[0] ?? null;
      if (singleArt) {
        persistSelectedAiSelfieArt(singleArt);
        const themedStation = enrichStationWithThemeBackgrounds(
          storedStation,
          singleArt
        );
        persistStationSelection(themedStation);
        dispatch(setPresetStation(themedStation));
        const artSlug = slugify(singleArt.name || "") || item.type;
        navigate(`/${artSlug}/idle`, {
          state: {
            selectedArt: singleArt,
            stationData: themedStation,
          },
        });
        return;
      }

      navigate(`/${item.type}/idle`);
    } catch {
      setOpeningCategory(null);
      navigate(`/${item.type}/all`);
    }
  };

  return (
    <section className="homeSecreen homeStationScreen">
      {categoriesLoading && <Loader />}

      <div className="homeStationContent">
        <h1 className="heading">MOSIDA SELFIE STATION</h1>
        {!categoriesLoading && categoriesError && (
          <p className="stationMessage">{categoriesError}</p>
        )}
        {!categoriesLoading && !categoriesError && (
          <div
            className="homeStationGrid"
            style={{
              gridTemplateRows: `repeat(${Math.max(
                1,
                Math.ceil(stations.length / 2),
              )}, minmax(0, 1fr))`,
            }}
          >
            {stations.map((item) => (
              <div className="homeStationGridItem" key={item.id}>
                <div
                  className="singleStation"
                  role="button"
                  tabIndex={0}
                  onClick={() => openCategory(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openCategory(item);
                    }
                  }}
                >
                  <div
                    className="stationMedia"
                    style={{ backgroundImage: `url("${item.imgURL}")` }}
                  >
                    <img
                      src={item.imgURL}
                      alt={item.name}
                      className="stationImgHome"
                      draggable={false}
                    />
                  </div>
                  <div className="stationFooter">
                    <div className="name">{item.name}</div>
                    <button
                      className="btnOpen"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCategory(item);
                      }}
                      disabled={!item.type || Boolean(openingCategory)}
                    >
                      OPEN
                      <span>
                        <Open />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Home;
